import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";

// Old blog URLs that were renamed — permanently redirect to the new slug.
const RENAMED_SLUGS: Record<string, string> = {
  "ai-assisted-content-workflow": "ai-content-creation-workflow",
};
import type { ReactNode } from "react";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { NewsletterSignup } from "@/components/newsletter-signup";
import type { Post } from "@/data/blog-posts";

function parseLinks(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
    }
    const [_, label, href] = match;
    if (href.startsWith("http") || href.startsWith("mailto:")) {
      parts.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer" className="underline decoration-brand-accent/60 underline-offset-4 hover:text-brand-accent">
          {label}
        </a>
      );
    } else {
      parts.push(
        <Link key={key++} to={href} className="underline decoration-brand-accent/60 underline-offset-4 hover:text-brand-accent">
          {label}
        </Link>
      );
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
  }
  return parts;
}

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }): Promise<Post> => {
    const moved = RENAMED_SLUGS[params.slug];
    if (moved) {
      throw redirect({ to: "/blog/$slug", params: { slug: moved }, statusCode: 301 });
    }
    const { posts } = await import("@/data/blog-posts");
    const post = posts.find((p) => p.slug === params.slug);
    if (!post) throw notFound();
    return post;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Article not found" }, { name: "robots", content: "noindex" }],
      };
    }
    return {
      meta: [
        { title: loaderData.metaTitle ?? `${loaderData.title} | MG Content Labs` },
        { name: "description", content: loaderData.metaDescription ?? loaderData.excerpt },
        { property: "og:title", content: loaderData.title },
        { property: "og:description", content: loaderData.metaDescription ?? loaderData.excerpt },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://mgcontentlabs.com/blog/${params.slug}` },
        { property: "og:image", content: `https://mgcontentlabs.com/blog-covers/${params.slug}.jpg` },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: loaderData.title },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: `https://mgcontentlabs.com/blog-covers/${params.slug}.jpg` },
        { name: "author", content: loaderData.author },
        ...(loaderData.keywords?.length
          ? [{ name: "keywords", content: loaderData.keywords.join(", ") }]
          : []),
      ],
      links: [{ rel: "canonical", href: `https://mgcontentlabs.com/blog/${params.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: loaderData.title,
            datePublished: loaderData.date,
            dateModified: loaderData.updated ?? loaderData.date,
            url: `https://mgcontentlabs.com/blog/${params.slug}`,
            author: { "@type": "Person", name: loaderData.author, description: loaderData.authorBio },
            image: loaderData.cover,
            description: loaderData.excerpt,
            keywords: loaderData.keywords?.join(", "),
            publisher: { "@type": "Organization", name: "MG Content Labs" },
            mainEntityOfPage: `https://mgcontentlabs.com/blog/${params.slug}`,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://mgcontentlabs.com/" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "https://mgcontentlabs.com/blog" },
              { "@type": "ListItem", position: 3, name: loaderData.title, item: `https://mgcontentlabs.com/blog/${params.slug}` },
            ],
          }),
        },
        ...(loaderData.faqs?.length
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: loaderData.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]
          : []),
      ],

    };
  },
  component: BlogPost,
  notFoundComponent: NotFound,
});

const slugify = (t: string) =>
  t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function NotFound() {
  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />
      <div className="mx-auto max-w-3xl px-6 py-32 text-center">
        <h1 className="font-serif text-5xl">Article not found</h1>
        <p className="mt-4 text-brand-deep/60">This post may have moved. Browse all insights below.</p>
        <Link to="/blog" className="mt-8 inline-block bg-brand-deep px-6 py-3 text-sm font-bold text-white">
          View all insights
        </Link>
      </div>
      <SiteFooter />
    </div>
  );
}

function BlogPost() {
  const post = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />
      <article className="mx-auto max-w-3xl px-6 py-20">
        <Link to="/blog" className="text-xs font-bold uppercase tracking-widest text-brand-accent">
          ← All insights
        </Link>
        <h1 className="mt-6 font-serif text-4xl leading-tight md:text-5xl">{post.title}</h1>
        <div className="mt-4 text-xs font-bold uppercase tracking-widest text-brand-deep/60">
          {post.category} · {new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} · {post.readTime}
        </div>
        <img
          src={post.cover}
          alt={post.title}
          width={1200}
          height={800}
          loading="eager"
          className="mt-10 aspect-[3/2] w-full object-cover outline outline-1 -outline-offset-1 outline-black/5"
        />
        <div className="prose-neutral mt-12 space-y-6 text-lg leading-relaxed text-brand-deep/80">
          {post.body.map((para: string, i: number) => (
            <p key={i}>{parseLinks(para)}</p>
          ))}
        </div>

        {post.takeaway ? (
          <aside className="mt-10 border-l-4 border-brand-accent bg-brand-deep/[0.03] p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">Key takeaway</p>
            <p className="mt-2 text-lg leading-relaxed text-brand-deep">{post.takeaway}</p>
          </aside>
        ) : null}

        {post.sections?.length ? (
          <nav aria-label="Table of contents" className="mt-12 border border-brand-deep/10 p-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-brand-deep/60">In this article</h2>
            <ol className="mt-4 space-y-2 text-sm">
              {post.sections.map((s) => (
                <li key={s.heading}>
                  <a href={`#${slugify(s.heading)}`} className="text-brand-deep/80 underline-offset-4 hover:text-brand-accent hover:underline">
                    {s.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        ) : null}

        {post.sections?.map((section) => (
          <section key={section.heading} id={slugify(section.heading)} className="mt-14 scroll-mt-28">
            <h2 className="font-serif text-3xl leading-snug">{section.heading}</h2>
            {section.paragraphs?.map((p, i) => (
              <p key={i} className="mt-4 text-lg leading-relaxed text-brand-deep/80">
                {parseLinks(p)}
              </p>
            ))}
            {section.bullets?.length ? (
              <ul className="mt-5 space-y-2">
                {section.bullets.map((b) => (
                  <li key={b} className="flex gap-3 text-brand-deep/80">
                    <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-accent" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {section.image ? (
              <figure className="mt-8">
                <img
                  src={section.image.src}
                  alt={section.image.alt}
                  width={1200}
                  height={750}
                  loading="lazy"
                  className="w-full object-cover outline outline-1 -outline-offset-1 outline-black/5"
                />
                {section.image.caption ? (
                  <figcaption className="mt-3 text-sm text-brand-deep/60">{section.image.caption}</figcaption>
                ) : null}
              </figure>
            ) : null}
            {section.subsections?.map((sub) => (
              <div key={sub.heading} className="mt-8">
                <h3 className="font-serif text-xl">{sub.heading}</h3>
                {sub.paragraphs?.map((p, i) => (
                  <p key={i} className="mt-2 leading-relaxed text-brand-deep/80">
                    {parseLinks(p)}
                  </p>
                ))}
                {sub.bullets?.length ? (
                  <ul className="mt-3 space-y-2">
                    {sub.bullets.map((b) => (
                      <li key={b} className="flex gap-3 text-brand-deep/80">
                        <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-accent/60" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </section>
        ))}

        {post.faqs?.length ? (
          <section id="faq" className="mt-16 scroll-mt-28 border-t border-brand-deep/10 pt-10">
            <h2 className="font-serif text-3xl">Frequently asked questions</h2>
            <dl className="mt-6 space-y-6">
              {post.faqs.map((f) => (
              <div key={f.q}>
                  <dt className="font-bold text-brand-deep">{f.q}</dt>
                  <dd className="mt-2 leading-relaxed text-brand-deep/80">{parseLinks(f.a)}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        {post.related?.length || post.sources?.length ? (
          <section className="mt-16 grid gap-10 border-t border-brand-deep/10 pt-10 md:grid-cols-2">
            {post.related?.length ? (
              <div>
                <h2 className="text-xs font-bold uppercase tracking-widest text-brand-deep/60">Keep reading</h2>
                <ul className="mt-4 space-y-2">
                  {post.related.map((r) => (
                    <li key={r.to}>
                      <a href={r.to} className="text-brand-deep underline decoration-brand-accent/40 underline-offset-4 hover:text-brand-accent">
                        {r.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {post.sources?.length ? (
              <div>
                <h2 className="text-xs font-bold uppercase tracking-widest text-brand-deep/60">Sources</h2>
                <ul className="mt-4 space-y-2">
                  {post.sources.map((s) => (
                    <li key={s.url}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-deep/80 underline decoration-brand-deep/20 underline-offset-4 hover:text-brand-accent"
                      >
                        {s.label} ↗
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>
        ) : null}


        <div className="mt-16 border-t border-brand-deep/10 pt-10">
          <h3 className="font-serif text-2xl">Ready to build a content engine like this?</h3>
          <p className="mt-2 text-brand-deep/60">
            Book a strategy call or explore our plans.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="bg-brand-deep px-6 py-3 text-sm font-bold text-white hover:bg-brand-accent">
              Book a Call
            </Link>
            <Link to="/pricing" className="border border-brand-deep px-6 py-3 text-sm font-bold text-brand-deep hover:bg-brand-deep hover:text-white">
              View Plans
            </Link>
          </div>
        </div>

        <div className="mt-12">
          <NewsletterSignup source={`blog-${post.slug}`} />
        </div>

        <div className="mt-12 flex items-start gap-4 rounded-sm border border-brand-deep/10 bg-brand-deep/[0.02] p-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-deep font-serif text-lg font-bold text-white">
            {post.author.split(" ").map((n) => n[0]).join("")}
          </div>
          <div>
            <p className="text-sm font-bold text-brand-deep">{post.author}</p>
            <p className="mt-1 text-sm leading-relaxed text-brand-deep/80">{post.authorBio}</p>
          </div>
        </div>
      </article>
      <SiteFooter />
    </div>
  );
}
